import type { AgentResponse, ToolData } from '../types';
import { RateLimitError } from './errors';
export { RateLimitError };

export class UnauthorizedError extends Error {
  constructor() {
    super('Session expired. Please complete the verification again.');
    this.name = 'UnauthorizedError';
  }
}

export class AccessDeniedError extends Error {
  constructor(msg?: string) {
    super(
      msg ||
        'Access denied. You do not have permission to perform this action.',
    );
    this.name = 'AccessDeniedError';
  }
}

/**
 * Real API client for the Solr Discovery Agent backend.
 *
 * Supports two modes:
 * - /chat2: Standard request/response (JSON)
 * - /chat3: Streaming (SSE via fetch ReadableStream)
 *
 * Pass the backend base URL via the baseUrl parameter to fetchAgentResponse / fetchStreamingResponse.
 */

let sessionId: string = crypto.randomUUID();
let boundSearchUrl: string | null = null;

/* ------------------------------------------------------------------ */
/*  Session helpers                                                    */
/* ------------------------------------------------------------------ */

/** Safe sessionStorage getter (no-op on SSR) */
function getSessionToken(): string {
  try {
    return (
      (typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem('sessionToken')) ||
      ''
    );
  } catch {
    return '';
  }
}

/** Build auth headers — omits X-Api-Key entirely when no token is present */
function buildAuthHeaders(): Record<string, string> {
  const token = getSessionToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token && token !== 'local-dev-bypass') {
    headers['X-Api-Key'] = token;
  }
  return headers;
}

/** Read the trace id exposed on a response, used to link feedback to a message. */
function readTraceId(res: Response): string | undefined {
  return (
    res.headers.get('traceId') ||
    res.headers.get('x-trace-id') ||
    res.headers.get('x-amzn-trace-id') ||
    undefined
  );
}

function attachSearchUrl(searchUrl?: string): string | undefined {
  const normalized = searchUrl?.trim();
  // Resolve via payload -> environment -> default
  const envCandidate =
    (typeof globalThis !== 'undefined' &&
      (globalThis as any).process?.env?.SOLR_BASE_URL) ||
    (typeof window !== 'undefined' && (window as any).SOLR_BASE_URL) ||
    'https://voyager.fas.usda.gov/voyager/solr/v0/select';

  const candidate = normalized || String(envCandidate || '').trim();
  if (!candidate) return undefined;

  // Bind on first session use. If already bound to the same value, allow reuse
  if (boundSearchUrl === null) {
    boundSearchUrl = candidate;
    try {
      // Helpful debug when diagnosing missing payload fields
      // eslint-disable-next-line no-console
      console.debug('[VES] Binding searchUrl for session:', boundSearchUrl);
    } catch {}
    return candidate;
  }

  if (boundSearchUrl === candidate) {
    return candidate;
  }

  return undefined;
}

/**
 * Silently attempt to get a session JWT from the verify endpoint
 * without a Turnstile token — used when CAPTCHA is disabled.
 *
 * Returns true if a token was successfully obtained and stored.
 * Returns false if the backend doesn't support anonymous sessions.
 */
export async function fetchAnonymousSession(
  verificationBaseUrl: string,
): Promise<boolean> {
  const baseUrl = verificationBaseUrl.replace(/\/$/, '');
  try {
    const res = await fetch(`${baseUrl}/verify-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bypass: true }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    const token: string | undefined = data?.session?.token;
    if (!token) return false;
    try {
      sessionStorage.setItem('sessionToken', token);
    } catch {
      // SSR — ignore
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Reset the session — call when user clicks "New Chat".
 * Optionally notifies the backend to clean up server-side state.
 */
export async function resetSession(baseUrl: string = ''): Promise<void> {
  const API_BASE_URL = baseUrl || 'http://localhost:3000';
  const oldSessionId = sessionId;
  sessionId = crypto.randomUUID();
  boundSearchUrl = null;

  // Fire-and-forget: tell the backend to clean up the old session
  try {
    await fetch(`${API_BASE_URL}/chat2`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: '',
        sessionId: oldSessionId,
        action: 'end_session',
      }),
    });
  } catch {
    // Best-effort cleanup — don't block the UI
  }
}

/**
 * Fire-and-forget warmup ping to wake the AgentCore runtime.
 * Called on app load so the agent is ready when the user types.
 */
export function warmupAgent(baseUrl: string = ''): void {
  const API_BASE_URL = baseUrl || 'http://localhost:3000';
  fetch(`${API_BASE_URL}/chat2`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: 'ping',
      sessionId: 'warmup-' + crypto.randomUUID(),
    }),
  }).catch(() => {});
}

/** Force a fresh sessionId without notifying the backend. */
export function newSessionId(): void {
  sessionId = crypto.randomUUID();
  boundSearchUrl = null;
}

/** Read the current sessionId. */
export function getSessionId(): string {
  return sessionId;
}

/** Restore a previously snapshotted sessionId. */
export function setSessionId(id: string): void {
  sessionId = id;
  boundSearchUrl = null;
}

/* ------------------------------------------------------------------ */
/*  TOOL_DATA extraction helper                                        */
/* ------------------------------------------------------------------ */

const TOOL_DATA_REGEX = /<!--\s*TOOL_DATA:([\s\S]*?)-->/;
const TOOL_DATA_MARKER = 'TOOL_DATA:';

function extractJsonObjectFromMarker(
  text: string,
  markerIndex: number,
): {
  jsonText?: string;
  removeStart: number;
  removeEnd: number;
} {
  const jsonStart = text.indexOf('{', markerIndex + TOOL_DATA_MARKER.length);
  if (jsonStart === -1) {
    return {
      removeStart: markerIndex,
      removeEnd: markerIndex + TOOL_DATA_MARKER.length,
    };
  }

  let inString = false;
  let escapeNext = false;
  let depth = 0;
  let jsonEnd = -1;

  for (let i = jsonStart; i < text.length; i += 1) {
    const ch = text[i];

    if (inString) {
      if (escapeNext) {
        escapeNext = false;
      } else if (ch === '\\') {
        escapeNext = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === '{') {
      depth += 1;
    } else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        jsonEnd = i;
        break;
      }
    }
  }

  if (jsonEnd === -1) {
    return { removeStart: markerIndex, removeEnd: text.length };
  }

  let removeStart = markerIndex;
  if (text.slice(Math.max(0, markerIndex - 4), markerIndex) === '<!--') {
    removeStart = markerIndex - 4;
  }

  let removeEnd = jsonEnd + 1;
  const tailMatch = text.slice(removeEnd).match(/^\s*-->/);
  if (tailMatch) {
    removeEnd += tailMatch[0].length;
  }

  return {
    jsonText: text.slice(jsonStart, jsonEnd + 1),
    removeStart,
    removeEnd,
  };
}

/**
 * Extract embedded TOOL_DATA from text content.
 * Returns { cleanText, toolData } where cleanText has the HTML comment removed
 * and toolData is the parsed JSON (or undefined if not found).
 */
export function extractToolData(text: string): {
  cleanText: string;
  toolData?: ToolData;
} {
  const markerIndex = text.indexOf(TOOL_DATA_MARKER);
  if (markerIndex !== -1) {
    const { jsonText, removeStart, removeEnd } = extractJsonObjectFromMarker(
      text,
      markerIndex,
    );
    if (jsonText) {
      try {
        const parsed = JSON.parse(jsonText) as ToolData;
        const cleanText =
          `${text.slice(0, removeStart)}${text.slice(removeEnd)}`.trim();
        return { cleanText, toolData: parsed };
      } catch {
        // Fallback to legacy regex flow below
      }
    }
  }

  const match = text.match(TOOL_DATA_REGEX);
  if (!match) return { cleanText: text };

  try {
    const parsed = JSON.parse(match[1]) as ToolData;
    const cleanText = text.replace(TOOL_DATA_REGEX, '').trim();
    return { cleanText, toolData: parsed };
  } catch {
    // Malformed JSON — return text as-is
    return { cleanText: text };
  }
}

/**
 * Remove leaked control tags that should never be shown in the chat UI.
 */
function sanitizeAssistantText(text: string): string {
  return text
    .replace(/<\/?system_warning>/gi, '')
    .replace(/<\/?system[^>]*>/gi, '')
    .replace(/^[ \t]*<\/?[^>]+>[ \t]*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Parse a JSON string that may be double-encoded.
 *
 * The backend sometimes sends SSE data as a JSON *string* containing a JSON
 * object, e.g.:  data: "{\"text\":\"Hello\"}"
 *
 * A single JSON.parse on that yields the *string* '{"text":"Hello"}' rather
 * than the object { text: "Hello" }.  This helper detects that case and
 * parses a second time so callers always get the final object.
 */
function parseSSEData<T = unknown>(raw: string): T {
  let result: unknown = JSON.parse(raw);
  // If the first parse produced a string, it was double-encoded — parse again
  if (typeof result === 'string') {
    result = JSON.parse(result);
  }
  return result as T;
}

/**
 * Parse JSON safely; returns original value when parsing fails.
 * Also handles values that are JSON-encoded more than once.
 */
function parseNestedJson(value: unknown, maxDepth = 3): unknown {
  let current = value;
  let depth = 0;

  while (typeof current === 'string' && depth < maxDepth) {
    const trimmed = current.trim();
    if (!trimmed) break;
    if (
      !(
        trimmed.startsWith('{') ||
        trimmed.startsWith('[') ||
        trimmed.startsWith('"')
      )
    ) {
      break;
    }

    try {
      current = JSON.parse(trimmed);
      depth += 1;
    } catch {
      break;
    }
  }

  return current;
}

function normalizeChartData(raw: unknown): ToolData['chart_data'] | undefined {
  if (!raw) return undefined;

  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    parsed = parseNestedJson(raw);
  }

  if (Array.isArray(parsed)) {
    const rows = parsed
      .map((item) => {
        const row = item as { label?: unknown; value?: unknown };
        if (typeof row.label !== 'string') return null;
        const value = Number(row.value);
        if (!Number.isFinite(value)) return null;
        return { label: row.label, value };
      })
      .filter(
        (item): item is { label: string; value: number } => item !== null,
      );
    return rows.length > 0 ? rows : undefined;
  }

  if (parsed && typeof parsed === 'object') {
    const rows = Object.entries(parsed as Record<string, unknown>)
      .map(([label, value]) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return null;
        return { label, value: n };
      })
      .filter(
        (item): item is { label: string; value: number } => item !== null,
      );
    return rows.length > 0 ? rows : undefined;
  }

  return undefined;
}

function toToolData(raw: unknown): ToolData | undefined {
  if (!raw || typeof raw !== 'object') return undefined;

  const obj = raw as Record<string, unknown>;
  const generated_query_url =
    (typeof obj.generated_query_url === 'string' && obj.generated_query_url) ||
    (typeof obj.generatedQueryUrl === 'string' && obj.generatedQueryUrl) ||
    undefined;

  const chart_data = normalizeChartData(obj.chart_data ?? obj.chartData);
  const chart_title =
    (typeof obj.chart_title === 'string' && obj.chart_title) ||
    (typeof obj.chartTitle === 'string' && obj.chartTitle) ||
    undefined;

  const suggestions = Array.isArray(obj.suggestions)
    ? obj.suggestions.filter((s): s is string => typeof s === 'string')
    : undefined;

  const document_data =
    obj.document_data && typeof obj.document_data === 'object'
      ? (obj.document_data as ToolData['document_data'])
      : obj.documentData && typeof obj.documentData === 'object'
        ? (obj.documentData as ToolData['document_data'])
        : undefined;

  const hasKnownFields =
    Boolean(generated_query_url) ||
    Boolean(chart_data && chart_data.length) ||
    Boolean(document_data) ||
    Boolean(suggestions && suggestions.length);

  if (!hasKnownFields) return undefined;

  return {
    generated_query_url: generated_query_url || '',
    chart_data: chart_data || [],
    ...(chart_title ? { chart_title } : {}),
    ...(document_data ? { document_data } : {}),
    ...(suggestions && suggestions.length > 0 ? { suggestions } : {}),
  };
}

function findToolDataDeep(raw: unknown, depth = 0): ToolData | undefined {
  if (depth > 5 || raw == null) return undefined;

  const direct = toToolData(raw);
  if (direct) return direct;

  if (typeof raw === 'string') {
    const parsed = parseNestedJson(raw);
    if (parsed !== raw) return findToolDataDeep(parsed, depth + 1);
    return undefined;
  }

  if (Array.isArray(raw)) {
    for (const item of raw) {
      const found = findToolDataDeep(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }

  if (typeof raw === 'object') {
    for (const value of Object.values(raw as Record<string, unknown>)) {
      const found = findToolDataDeep(value, depth + 1);
      if (found) return found;
    }
  }

  return undefined;
}

/**
 * Resolve assistant text when payloads are wrapped multiple times,
 * including event/data envelopes nested inside response.content strings.
 */
function extractTextContent(value: unknown, maxDepth = 4): string | undefined {
  let current = parseNestedJson(value);

  for (let depth = 0; depth < maxDepth; depth += 1) {
    if (typeof current === 'string') {
      const parsed = parseNestedJson(current);
      if (parsed === current) {
        return current;
      }
      current = parsed;
      continue;
    }

    if (!current || typeof current !== 'object') {
      return undefined;
    }

    const obj = current as Record<string, unknown>;

    // Handle event envelope shape: { event: 'response', data: ... }
    if ('data' in obj) {
      current = parseNestedJson(obj.data);
      continue;
    }

    // Handle common response shapes
    if ('response' in obj) {
      current = parseNestedJson(obj.response);
      continue;
    }

    if (typeof obj.content === 'string') {
      current = obj.content;
      continue;
    }

    return undefined;
  }

  return typeof current === 'string' ? current : undefined;
}

/**
 * Normalize multiple backend response shapes into the UI contract.
 * Supports standard { response: { content } } and event-wrapped payloads like
 * { event: 'response', data: '{"response":"text"}' }.
 */
function normalizeBackendResponse(payload: unknown): {
  content: string;
  toolData?: ToolData;
  sessionId?: string;
} {
  const outer = parseNestedJson(payload) as Record<string, unknown>;
  const eventData =
    outer && typeof outer === 'object' && 'event' in outer && 'data' in outer
      ? parseNestedJson(outer.data)
      : undefined;

  const candidate = (eventData ?? outer) as Record<string, unknown>;
  const responseField = parseNestedJson(candidate?.response);

  let content = 'No response from agent.';
  let toolData: ToolData | undefined;

  if (typeof responseField === 'string') {
    content = extractTextContent(responseField) ?? responseField;
  } else if (responseField && typeof responseField === 'object') {
    const responseObj = responseField as Record<string, unknown>;
    if (typeof responseObj.content === 'string') {
      content = extractTextContent(responseObj.content) ?? responseObj.content;
    }
    if (responseObj.tool_data && typeof responseObj.tool_data === 'object') {
      toolData = responseObj.tool_data as ToolData;
    }
  }

  if (
    content === 'No response from agent.' &&
    typeof candidate?.response === 'string'
  ) {
    content = extractTextContent(candidate.response) ?? candidate.response;
  }

  if (
    !toolData &&
    candidate?.tool_data &&
    typeof candidate.tool_data === 'object'
  ) {
    toolData = candidate.tool_data as ToolData;
  }

  if (!toolData) {
    toolData =
      findToolDataDeep(candidate) ||
      findToolDataDeep(responseField) ||
      findToolDataDeep(eventData) ||
      findToolDataDeep(outer);
  }

  const extracted = extractToolData(content);

  return {
    content: sanitizeAssistantText(extracted.cleanText),
    toolData: toolData ?? extracted.toolData,
    sessionId:
      typeof candidate?.sessionId === 'string'
        ? candidate.sessionId
        : typeof outer?.sessionId === 'string'
          ? outer.sessionId
          : undefined,
  };
}

/* ------------------------------------------------------------------ */
/*  /chat2 — JSON request/response                                     */
/* ------------------------------------------------------------------ */

/**
 * Send a message to the agent via /chat2 (JSON request/response).
 * Automatically retries on 503 (AgentCore cold start) up to 2 times.
 *
 * @param baseUrl - The backend base URL (e.g. block's agentEndpoint setting)
 * @param onRetry - Optional callback invoked before each retry with (attempt, maxRetries)
 */
export async function fetchAgentResponse(
  userMessage: string,
  baseUrl: string = '',
  searchUrl?: string,
  onRetry?: (attempt: number, maxRetries: number) => void,
): Promise<AgentResponse> {
  const API_BASE_URL = baseUrl || 'http://localhost:3000';
  const maxRetries = 2;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      onRetry?.(attempt, maxRetries);
      // Wait before retry: 3s first retry, 5s second retry
      await new Promise((r) => setTimeout(r, attempt === 1 ? 3000 : 5000));
    }

    try {
      const payload: Record<string, unknown> = {
        message: userMessage,
        sessionId,
      };
      const searchUrlValue = attachSearchUrl(searchUrl);
      if (searchUrlValue) {
        payload.searchUrl = searchUrlValue;
      }

      // eslint-disable-next-line no-console
      console.debug('[VES] POST /chat2 payload:', payload);
      const res = await fetch(`${API_BASE_URL}/chat2`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        throw new UnauthorizedError();
      }

      if (res.status === 403) {
        throw new AccessDeniedError(
          'You do not have permission to access this resource.',
        );
      }

      if (res.status === 429) {
        const errorBody = await res.json().catch(() => ({}));
        throw new RateLimitError(
          (errorBody as { error?: string; message?: string }).error ||
            (errorBody as { message?: string }).message,
        );
      }

      if (res.status === 503 && attempt < maxRetries) {
        lastError = new Error('Agent is warming up, retrying...');
        continue;
      }

      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        throw new Error(
          (errorBody as { error?: string }).error ||
            `API request failed with status ${res.status}`,
        );
      }

      // Fallback: if /chat2 somehow returns SSE, we can't handle it here.
      // The caller (App.tsx) does the content-type check before dispatching.
      const data = await res.json();
      const normalized = normalizeBackendResponse(data);

      // Update sessionId if the backend returned one
      if (normalized.sessionId) {
        sessionId = normalized.sessionId;
      }

      const traceId = readTraceId(res);

      return {
        id: `resp-${Date.now()}`,
        role: 'assistant',
        content: normalized.content,
        ...(normalized.toolData && { tool_data: normalized.toolData }),
        ...(traceId && { traceId }),
      };
    } catch (err) {
      if (
        err instanceof UnauthorizedError ||
        err instanceof AccessDeniedError ||
        err instanceof RateLimitError
      )
        throw err;
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt < maxRetries) continue;
    }
  }

  throw lastError ?? new Error('Failed to get response from agent');
}

/* ------------------------------------------------------------------ */
/*  /chat3 — SSE Streaming                                             */
/* ------------------------------------------------------------------ */

export interface StreamCallbacks {
  /** Called on every delta chunk with the accumulated text so far */
  onDelta: (textSoFar: string) => void;
  /** Called when the stream completes */
  onDone: (finalText: string, toolData?: ToolData, traceId?: string) => void;
  /** Called on error */
  onError: (error: Error) => void;
}

/**
 * Send a message to the agent via /chat3 (SSE streaming).
 * Uses fetch + ReadableStream (not EventSource) because we need
 * POST method and custom headers.
 *
 * Returns an AbortController — call .abort() to cancel the stream
 * (e.g. "Stop generating" button).
 *
 * SSE format expected (note: data may be single OR double-encoded JSON):
 *   event: delta
 *   data: {"text":"Hello"}          ← single-encoded
 *   data: "{\"text\":\"Hello\"}"    ← double-encoded (string-wrapped)
 *   ...
 *   event: done
 *   data: {"sessionId":"uuid"}
 */
export function fetchStreamingResponse(
  userMessage: string,
  callbacks: StreamCallbacks,
  baseUrl: string = '',
  searchUrl?: string,
): AbortController {
  const API_BASE_URL = baseUrl || 'http://localhost:3000';
  const controller = new AbortController();

  const body: Record<string, unknown> = { message: userMessage, sessionId };
  const searchUrlValue = attachSearchUrl(searchUrl);
  if (searchUrlValue) {
    body.searchUrl = searchUrlValue;
  }

  const doStream = async () => {
    try {
      // eslint-disable-next-line no-console
      console.debug('[VES] POST /chat3 payload:', body);
      const res = await fetch(`${API_BASE_URL}/chat3`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (res.status === 401) {
        throw new UnauthorizedError();
      }

      if (res.status === 403) {
        throw new AccessDeniedError();
      }

      if (res.status === 429) {
        const text = await res.text().catch(() => '');
        throw new RateLimitError(text || undefined);
      }

      if (!res.ok) {
        const errorBody = await res.text().catch(() => '');
        throw new Error(
          errorBody || `Streaming request failed with status ${res.status}`,
        );
      }

      const traceId = readTraceId(res);

      // Fallback: if /chat3 returns JSON instead of SSE, handle gracefully
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await res.json();
        const normalized = normalizeBackendResponse(data);
        if (normalized.sessionId) sessionId = normalized.sessionId;
        callbacks.onDelta(normalized.content);
        callbacks.onDone(normalized.content, normalized.toolData, traceId);
        return;
      }

      // Parse SSE from ReadableStream
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let latestToolData: ToolData | undefined;
      let sseBuffer = ''; // accumulates partial SSE lines
      let currentEvent = ''; // tracks the current event type

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = sseBuffer.split('\n');
        // Keep the last (possibly incomplete) line in the buffer
        sseBuffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();

          if (trimmed === '') {
            // Empty line = end of SSE event block, reset event type
            currentEvent = '';
            continue;
          }

          if (trimmed.startsWith('event:')) {
            currentEvent = trimmed.slice(6).trim();
            continue;
          }

          if (trimmed.startsWith('data:')) {
            const dataStr = trimmed.slice(5).trim();

            if (currentEvent === 'delta') {
              try {
                const parsed = parseSSEData<{ text?: string }>(dataStr);
                if (parsed.text) {
                  textBuffer += parsed.text;
                  callbacks.onDelta(textBuffer);
                }
              } catch {
                // Skip malformed delta
              }
            } else if (currentEvent === 'response') {
              try {
                const normalized = normalizeBackendResponse(dataStr);
                if (
                  normalized.content &&
                  normalized.content !== 'No response from agent.'
                ) {
                  textBuffer = normalized.content;
                  callbacks.onDelta(textBuffer);
                }
                if (normalized.toolData) {
                  latestToolData = normalized.toolData;
                }
                if (normalized.sessionId) {
                  sessionId = normalized.sessionId;
                }
              } catch {
                // Skip malformed response event
              }
            } else if (currentEvent === 'done') {
              try {
                const normalized = normalizeBackendResponse(dataStr);
                if (normalized.sessionId) {
                  sessionId = normalized.sessionId;
                }
                if (normalized.toolData) {
                  latestToolData = normalized.toolData;
                }
                if (
                  normalized.content &&
                  normalized.content !== 'No response from agent.' &&
                  !textBuffer
                ) {
                  textBuffer = normalized.content;
                  callbacks.onDelta(textBuffer);
                }
              } catch {
                // Skip malformed done
              }

              // Stream complete — extract TOOL_DATA from the final text
              const { cleanText, toolData } = extractToolData(textBuffer);
              callbacks.onDone(
                sanitizeAssistantText(cleanText),
                latestToolData ?? toolData,
                traceId,
              );
              return;
            }
          }
        }
      }

      // If we exit the loop without a done event, finalize anyway
      const { cleanText, toolData } = extractToolData(textBuffer);
      callbacks.onDone(
        sanitizeAssistantText(cleanText),
        latestToolData ?? toolData,
        traceId,
      );
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // User clicked "Stop generating" — this is expected, not an error
        return;
      }
      callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  doStream();
  return controller;
}

/* ------------------------------------------------------------------ */
/*  Document session (unchanged)                                       */
/* ------------------------------------------------------------------ */

/**
 * Initialize a frontend-driven document chat session bound to a single PDF.
 * Creates a NEW session and tells the agent to load the PDF text into its
 * system prompt so all subsequent messages answer from that document only.
 *
 * The caller is responsible for resetting the local sessionId first
 * (call resetSession()) so this init doesn't share state with a prior chat.
 *
 * @param endpoint - Which backend route to use ('/chat2' or '/chat3').
 *                   Defaults to '/chat3' to match the current selected endpoint.
 */
export async function initDocumentSession(
  documentUrl: string,
  documentTitle?: string,
  endpoint: '/chat2' | '/chat3' = '/chat3',
  baseUrl: string = '',
): Promise<{
  title: string;
  pages: number;
  url: string;
}> {
  const API_BASE_URL = baseUrl || 'http://localhost:3000';
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Key': getSessionToken(),
    },
    body: JSON.stringify({
      action: 'init_document_session',
      documentUrl,
      // Forwarded as a fallback when the PDF metadata has no Title field —
      // the host page card already knows the human-friendly title from Solr.
      documentTitle,
      sessionId,
    }),
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(
      (errorBody as { error?: string }).error ||
        `Document session init failed with status ${res.status}`,
    );
  }

  const data = (await res.json()) as {
    response?: {
      type?: string;
      document?: { title: string; pages: number; url: string };
    };
  };

  if (data.response?.type !== 'documentLoaded' || !data.response.document) {
    throw new Error('Unexpected response from document session init');
  }

  return data.response.document;
}
